import { inject, ElementRef, viewChildren, Directive, computed, effect, signal, untracked } from "@angular/core";
import type { WkDataTableInternalDataItem } from "@components/data-table/data-table.types";
import { WkDataTableDraggingService, WkDataTableItemsService } from "@components/data-table/data-table.components";
import { WkDataTableContextService } from "@components/data-table/data-table-context.service";

@Directive()
export abstract class WkDataTable<TItem> {
    protected readonly dataTableDraggingService = inject(WkDataTableDraggingService);
    protected readonly dataTableContext = inject<WkDataTableContextService<TItem>>(WkDataTableContextService);
    protected readonly dataTableItemsService = inject<WkDataTableItemsService<TItem>>(WkDataTableItemsService);

    protected tableRows = viewChildren<ElementRef<HTMLElement>>("tableRow");
    protected rowHeight = signal<number>(37.8);
    private isRowHeightSet = signal<boolean>(false);

    protected readonly items = this.dataTableItemsService.items;

    protected trackByPrimaryField = (_: number, item: WkDataTableInternalDataItem<TItem>): string => {
        const primaryField = this.dataTableContext.primaryField();
        if (!item || !primaryField) return "";
        const primaryFieldValue = item.value[primaryField];
        return String(primaryFieldValue);
    };

    protected primaryField = computed(() => this.dataTableContext.primaryField() ?? "");

    constructor() {
        effect(() => {
            const rows = this.tableRows();
            const isDragging = this.dataTableDraggingService.isDragging();
            const isRowHeightSet = this.isRowHeightSet();
            if (!rows?.length || isDragging || isRowHeightSet) return;

            const row = rows[0];
            const rowEl = row.nativeElement;
            const height = rowEl.getBoundingClientRect().height;
            this.rowHeight.set(height);
            untracked(() => this.isRowHeightSet.set(true));
        });
    }
}
